import React, { useEffect, useState } from 'react';
import { getReports, getReport, MOCK_RESULT, MOCK_SC_RESULT, CATEGORIES } from '../api';

const DEMO_REPORTS = [
  {
    id: 'demo-qr',
    isDemo: true,
    product_id: 'quick_report',
    category: 'qsr',
    lat: -26.0274,
    lon: 28.0106,
    radius_km: 3,
    total_score: 76,
    verdict: 'Proceed',
    verdict_color: 'green',
    created_at: null,
    label: 'Sample Quick Report',
    sublabel: 'QSR — Fourways, Johannesburg',
    mockData: MOCK_RESULT,
    request: { category: 'qsr', radius_km: 3, format: 'drive_thru', anchor: 'Shoprite', own_brand: 'KFC' },
  },
  {
    id: 'demo-sc',
    isDemo: true,
    product_id: 'sc_feasibility',
    category: 'shopping_centre',
    lat: -33.9258,
    lon: 18.4232,
    radius_km: 5,
    total_score: 72,
    verdict: 'Proceed',
    verdict_color: 'green',
    created_at: null,
    label: 'Sample Feasibility Study',
    sublabel: 'Shopping Centre — Cape Town',
    mockData: MOCK_SC_RESULT,
    request: { category: 'shopping_centre', radius_km: 5 },
  },
];

export default function MyReportsPanel({ onViewReport, isMobile }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getReports()
      .then(setReports)
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, []);

  const handleViewDemo = (demo) => {
    onViewReport(demo.mockData, demo.request, { lat: demo.lat, lon: demo.lon });
  };

  const handleView = async (id) => {
    try {
      const full = await getReport(id);
      if (full?.report_data) {
        onViewReport(full.report_data, {
          category: full.category, radius_km: full.radius_km,
          format: full.format, anchor: full.anchor, own_brand: full.own_brand,
        }, { lat: full.lat, lon: full.lon });
      }
    } catch (err) {
      console.error('Failed to load report:', err);
    }
  };

  const categoryLabel = (key) => {
    if (key === 'shopping_centre') return 'Shopping Centre Feasibility';
    const cat = CATEGORIES.find(c => c.key === key);
    return cat ? cat.label : key;
  };

  const scoreColor = (s) => {
    if (s >= 80) return 'var(--green)';
    if (s >= 65) return 'var(--amber)';
    return 'var(--red)';
  };

  const verdictBg = (c) => ({
    green: 'var(--green)', amber: 'var(--amber)', red: 'var(--red)',
  }[c] || 'var(--muted)');

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>My Reports</div>
      </div>

      {/* Demo reports */}
      <div style={styles.sectionLabel}>Sample Reports</div>
      <div style={styles.sectionHint}>View sample outputs to understand what each report delivers.</div>
      <div style={styles.demoGrid}>
        {DEMO_REPORTS.map(d => (
          <div key={d.id} style={styles.demoCard} onClick={() => handleViewDemo(d)}>
            <div style={styles.demoBadge}>SAMPLE</div>
            <div style={styles.demoScore}>
              <span style={{ color: scoreColor(d.total_score), fontFamily: "'News Cycle',serif", fontSize: 28, fontWeight: 700 }}>
                {d.total_score}
              </span>
              <span style={styles.demoScoreSuffix}>/100</span>
            </div>
            <div style={styles.demoLabel}>{d.label}</div>
            <div style={styles.demoSublabel}>{d.sublabel}</div>
            <div style={styles.demoAction}>View sample report</div>
          </div>
        ))}
      </div>

      {/* Actual reports */}
      <div style={{ ...styles.sectionLabel, marginTop: 32 }}>Your Reports</div>
      {loading ? (
        <div style={styles.empty}>Loading...</div>
      ) : reports.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyText}>No reports yet. Run your first report from the New Report tab.</div>
        </div>
      ) : (
        <div style={styles.list}>
          {reports.map(r => (
            <div key={r.id} style={styles.card} onClick={() => handleView(r.id)}>
              <div style={styles.cardTop}>
                <div style={styles.cardScore}>
                  <span style={{ color: scoreColor(r.total_score), fontFamily: "'News Cycle',serif", fontSize: 24, fontWeight: 700 }}>
                    {r.total_score}
                  </span>
                  <span style={styles.scoreLabel}>/100</span>
                </div>
                <div style={styles.cardMeta}>
                  <div style={styles.cardCategory}>{categoryLabel(r.category)}</div>
                  <div style={styles.cardLocation}>
                    {r.lat.toFixed(4)}, {r.lon.toFixed(4)} · {r.radius_km}km
                  </div>
                </div>
                <div style={{ ...styles.verdictPill, background: verdictBg(r.verdict_color) + '18', color: verdictBg(r.verdict_color) }}>
                  {r.verdict}
                </div>
              </div>
              <div style={styles.cardBottom}>
                <span style={styles.cardDate}>
                  {new Date(r.created_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <span style={styles.viewBtn}>View report</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    background: 'var(--cream)',
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottom: '1px solid var(--rule)',
  },
  title: {
    fontFamily: "'News Cycle', serif",
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--ink)',
  },
  sectionLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    marginBottom: 4,
  },
  sectionHint: {
    fontSize: 12,
    color: 'var(--muted)',
    marginBottom: 12,
  },
  demoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    maxWidth: 700,
  },
  demoCard: {
    background: 'var(--white)',
    border: '2px dashed var(--rule)',
    borderRadius: 8,
    padding: '20px 16px',
    cursor: 'pointer',
    textAlign: 'center',
    position: 'relative',
    transition: 'border-color 0.15s',
  },
  demoBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    fontFamily: "'DM Mono', monospace",
    fontSize: 9,
    letterSpacing: '1px',
    color: 'var(--ink)',
    background: 'var(--s-w06)',
    padding: '2px 8px',
    borderRadius: 10,
    fontWeight: 600,
  },
  demoScore: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 2,
    marginBottom: 8,
  },
  demoScoreSuffix: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    color: 'var(--muted)',
  },
  demoLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--ink)',
    marginBottom: 2,
  },
  demoSublabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    color: 'var(--muted)',
  },
  demoAction: {
    marginTop: 12,
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--ink)',
    fontFamily: "'Work Sans', sans-serif",
  },
  empty: {
    textAlign: 'center',
    padding: '30px 20px',
  },
  emptyText: {
    fontSize: 13,
    color: 'var(--muted)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    maxWidth: 700,
    marginTop: 12,
  },
  card: {
    background: 'var(--white)',
    border: '1px solid var(--rule)',
    borderRadius: 6,
    padding: '16px 20px',
    cursor: 'pointer',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  cardScore: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 2,
    flexShrink: 0,
  },
  scoreLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    color: 'var(--muted)',
  },
  cardMeta: {
    flex: 1,
    minWidth: 0,
  },
  cardCategory: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--ink)',
  },
  cardLocation: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    color: 'var(--muted)',
    marginTop: 2,
  },
  verdictPill: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    flexShrink: 0,
    fontFamily: "'DM Mono', monospace",
    letterSpacing: '0.3px',
  },
  cardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 10,
    borderTop: '1px solid var(--rule)',
  },
  cardDate: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    color: 'var(--muted)',
  },
  viewBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--ink)',
    fontFamily: "'Work Sans', sans-serif",
  },
};
